import React from 'react';
import classnames from 'classnames';
import get from 'lodash/get';
import noop from 'lodash/noop';
import {
  DASHBOARD_URL,
  DASHBOARD_SHARED_URL,
  TEMP_DASHBOARD_URL,
  DASHBOARD_TYPE,
  HOME_LONG_PATH,
} from '@utils/constants';
import featureNames from '@utils/featureToggle/constants';
import DashboardTab from './DashboardTab';
import styles from './index.less';

const TemporaryDashboardTabs = ({
  dashboards,
  compareDashboards,
  removeCompareDashboard,
  activeId,
  remove,
  replace,
  init,
  hasChangedMap,
  changeSaveDashboardPromptVisibility,
  setActiveDashboardId,
  setDefaultLandingPage,
  showDefaultLandingPagePrompt,
  setDefaultLandingPagePromptTitle,
  setDefaultLandingPagePromptContent,
  setDefaultLandingPagePromptNeedInput,
  setDefaultLandingPagePromptOkFn,
  toggleUpdatePopupVisible,
  renderSharedTab,
  sharedTabs,
  defaultLandingPage,
  features,
}) => {
  React.useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeDashboard = removeCompareDashboard;
  function urlOfTab(dashboard, type) {
    const { id, isSaved, isSharing } = dashboard;
    let url = HOME_LONG_PATH;
    if (isSharing) {
      url = `${DASHBOARD_SHARED_URL[type]}${id}`;
    } else if (isSaved && DASHBOARD_URL[type]) {
      url = `${DASHBOARD_URL[type]}${id}`;
    } else if (TEMP_DASHBOARD_URL[type]) {
      url = `${TEMP_DASHBOARD_URL[type]}${id}`;
    }
    return url;
  }
  function chkDefaultIdx(arr) {
    let defaultIdx = -1;
    for (let i = 0, len = arr.length; i < len; i += 1) {
      const el = arr[i];
      const type = el.dashboardTypeId || el.type || (el.dashboardType && el.dashboardType.id);
      const u = urlOfTab(el, type);
      if ((defaultLandingPage.url || '').endsWith(u)) {
        defaultIdx = i;
        break;
      }
    }
    return defaultIdx;
  }
  function makeSpecificEleLeft(arr, idx) {
    if (idx > 0) {
      const e = arr[idx];
      arr.splice(idx, 1);
      arr.unshift(e);
    }
    return arr;
  }
  function buildTabs(arr, type) {
    return arr.map((dashboard) => (
      <DashboardTab
        key={dashboard.id}
        id={dashboard.id}
        dashboards={arr}
        dashboard={{
          ...dashboard,
          title: dashboard.name,
          type,
        }}
        activeId={activeId}
        isSaved={!!dashboard.isSaved}
        setActiveDashboardId={setActiveDashboardId}
        setDefaultLandingPage={setDefaultLandingPage}
        showDefaultLandingPagePrompt={showDefaultLandingPagePrompt}
        setDefaultLandingPagePromptTitle={setDefaultLandingPagePromptTitle}
        setDefaultLandingPagePromptContent={setDefaultLandingPagePromptContent}
        setDefaultLandingPagePromptNeedInput={setDefaultLandingPagePromptNeedInput}
        setDefaultLandingPagePromptOkFn={setDefaultLandingPagePromptOkFn}
        isActive={`${activeId}` === `${dashboard.id}`}
        prevId={arr[arr.length - 1].id || -1}
        remove={removeDashboard}
        replace={replace}
        toggleUpdatePopupVisible={changeSaveDashboardPromptVisibility}
        className={classnames({ [styles.unsaved]: dashboard.unsaved || dashboard.hasChanged })}
        defaultLandingPage={defaultLandingPage}
      />
    ));
  }
  // rearrange tabs to make default dashboard closer to left.
  const filteredCompareDashboards = compareDashboards.filter(
    (e) => !e.isSharing && e.dashboardTypeId === 1,
  );
  const combDashboards = filteredCompareDashboards.concat(dashboards);

  const filteredCompareDashboardsTabs = buildTabs(
    filteredCompareDashboards,
    DASHBOARD_TYPE.COMPARE,
  );

  const dashboardsTabs = dashboards
    .filter((dashboard) => {
      const isAlertDashboard = dashboard.type === DASHBOARD_TYPE.ALERT_DIAGNOSTICS;
      if (!features[featureNames.ALERT_DIAGNOSTICS] && isAlertDashboard) {
        return false;
      }
      return true;
    })
    .map((dashboard, index) => {
      const needSave =
        !!hasChangedMap[dashboard.key] &&
        ![
          DASHBOARD_TYPE.ALERT_DIAGNOSTICS,
          DASHBOARD_TYPE.AI_ALERT_DIAGNOSTICS,
          DASHBOARD_TYPE.NOC_DRILLDOWN,
          DASHBOARD_TYPE.VIEWER_MODULE,
          DASHBOARD_TYPE.EXPERIENCE_ALERT_DIAGNOSTICS,
          DASHBOARD_TYPE.CUSTOM_DASHBOARD_DRILLDOWN,
        ].includes(dashboard.type);
      return (
        <DashboardTab
          key={`${dashboard.isSaved ? 'saved-' : ''}${dashboard.id}-${dashboard.updateTime}`}
          id={dashboard.id}
          dashboards={dashboards}
          dashboard={{
            ...dashboard,
            title: dashboard.title,
          }}
          activeId={activeId}
          isActive={`${activeId}` === `${dashboard.id}`}
          prevDashboard={get(dashboards, `[${index - 1}]`, null)}
          remove={remove}
          replace={replace}
          isSaved={!!dashboard.isSaved}
          setActiveDashboardId={setActiveDashboardId}
          setDefaultLandingPage={setDefaultLandingPage}
          showDefaultLandingPagePrompt={showDefaultLandingPagePrompt}
          setDefaultLandingPagePromptTitle={setDefaultLandingPagePromptTitle}
          setDefaultLandingPagePromptContent={setDefaultLandingPagePromptContent}
          setDefaultLandingPagePromptNeedInput={setDefaultLandingPagePromptNeedInput}
          setDefaultLandingPagePromptOkFn={setDefaultLandingPagePromptOkFn}
          className={classnames({
            [styles.unsaved]: needSave,
          })}
          toggleUpdatePopupVisible={toggleUpdatePopupVisible}
          defaultLandingPage={defaultLandingPage}
        />
      );
    });
  const combDashboardsTabs = filteredCompareDashboardsTabs.concat(dashboardsTabs);

  const defaultIdx = chkDefaultIdx(combDashboards);
  const rearrangedTabs = makeSpecificEleLeft(combDashboardsTabs, defaultIdx);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sharedTabsEle = React.useMemo(() => renderSharedTab(styles), [sharedTabs.length, activeId]);
  return (
    <ul className={styles.tabsWrapper}>
      {/* temp and saved dashboards */}
      {rearrangedTabs}

      {/* shared dashboards */}
      {sharedTabsEle}
    </ul>
  );
};

TemporaryDashboardTabs.defaultProps = {
  dashboards: [],
  compareDashboards: [],
  hasChangedMap: {},
  changeSaveDashboardPromptVisibility: noop,
  toggleUpdatePopupVisible: noop,
  setActiveDashboardId: noop,
  showDefaultLandingPagePrompt: noop,
  setDefaultLandingPage: noop,
  setDefaultLandingPagePromptTitle: noop,
  setDefaultLandingPagePromptContent: noop,
  setDefaultLandingPagePromptNeedInput: noop,
  setDefaultLandingPagePromptOkFn: noop,
  renderSharedTab: noop,
  sharedTabs: [],
  defaultLandingPage: {},
  features: {},
};

export default TemporaryDashboardTabs;
